/* about-animations.js — Agriko About Page scroll animation system */
(function () {
  'use strict';

  var page = document.querySelector('.ag-about-page');
  if (!page) return;

  /* ── Reduced motion: reveal everything immediately ─────────────────── */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    page.querySelectorAll('[data-reveal], [data-ag-fade]').forEach(function (el) {
      el.classList.add('is-visible');
    });
    page.querySelectorAll('.ag-heading-split').forEach(function (el) {
      el.classList.add('ag-words-animate');
    });
    var tl = page.querySelector('.ag-about-timeline');
    if (tl) tl.classList.add('ag-spine-live');
    return;
  }

  var EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

  /* ── 1. IntersectionObserver for card/element reveals ──────────────── */
  var revealObs = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) {
        e.target.classList.add('is-visible');
        revealObs.unobserve(e.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -50px 0px' });

  /* Cards already have data-reveal + inline transition-delay stagger */
  var cardSels = [
    '.ag-testimonial-card[data-reveal]',
    '.ag-ingredient-card[data-reveal]',
    '.ag-timeline-item[data-reveal]',
    '.ag-value-card[data-reveal]',
    '.ag-about-founder__story[data-reveal]',
    '.ag-about-founder__media[data-reveal]'
  ];
  cardSels.forEach(function (sel) {
    page.querySelectorAll(sel).forEach(function (el) {
      revealObs.observe(el);
    });
  });

  /* ── 2. Secondary fade targets (no existing data-reveal) ───────────── */
  var fadeDefs = [
    { sel: '.ag-about-social-proof__quote', delay: 120 },
    { sel: '.ag-about-social-proof__item', stagger: 110 },
    { sel: '.ag-about-testimonials__subheading', delay: 80 },
    { sel: '.ag-about-ingredients__subheading', delay: 80 },
    { sel: '.ag-about-cta__stat', delay: 60 },
    { sel: '.ag-about-cta__buttons', delay: 120 },
    { sel: '.ag-about-cta__badges', delay: 200 }
  ];

  fadeDefs.forEach(function (def) {
    page.querySelectorAll(def.sel).forEach(function (el, i) {
      el.setAttribute('data-ag-fade', '');
      var d = def.stagger ? i * def.stagger : (def.delay || 0);
      if (d) el.style.transitionDelay = d + 'ms';
      revealObs.observe(el);
    });
  });

  /* ── 3. Word-split section headings ────────────────────────────────── */
  var HEADING_SELS = [
    '.ag-about-social-proof__title',
    '.ag-about-testimonials__heading',
    '.ag-about-founder__title',
    '.ag-about-ingredients__heading',
    '.ag-about-timeline__heading',
    '.ag-about-cta__heading'
    /* ag-about-mission__heading intentionally excluded — has colored child spans */
  ];

  var wordObs = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) {
        e.target.classList.add('ag-words-animate');
        wordObs.unobserve(e.target);
      }
    });
  }, { threshold: 0.35 });

  HEADING_SELS.forEach(function (sel) {
    page.querySelectorAll(sel).forEach(function (el) {
      var text = el.textContent.trim();
      if (!text) return;
      el.setAttribute('aria-label', text);
      el.innerHTML = text.split(/\s+/).map(function (word, i) {
        return '<span class="ag-w" style="--wi:' + i + '" aria-hidden="true">' + word + '</span>';
      }).join(' ');
      el.classList.add('ag-heading-split');
      wordObs.observe(el);
    });
  });

  /* Mission heading: simple fade-up (preserve its colored spans) */
  page.querySelectorAll('.ag-about-mission__heading').forEach(function (el) {
    el.setAttribute('data-ag-fade', '');
    revealObs.observe(el);
  });

  /* ── 4. Timeline spine draw ────────────────────────────────────────── */
  var timelineEl = page.querySelector('.ag-about-timeline');
  if (timelineEl) {
    var spineObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          timelineEl.classList.add('ag-spine-live');
          spineObs.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    spineObs.observe(timelineEl);
  }

  /* ── 5. Number counter animation ───────────────────────────────────── */
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function runCounter(el) {
    var raw = el.textContent;
    var m = raw.match(/([\d,]+)(\+?)/);
    if (!m) return;
    var target = parseInt(m[1].replace(/,/g, ''), 10);
    var plus = m[2];
    var before = raw.slice(0, m.index);
    var after = raw.slice(m.index + m[0].length);
    var dur = 1800;
    var t0 = performance.now();

    function fmt(n) {
      return n >= 1000 ? n.toLocaleString('en-US') : String(n);
    }

    (function tick(now) {
      var p = Math.min((now - t0) / dur, 1);
      el.textContent = before + fmt(Math.round(easeOutCubic(p) * target)) + plus + after;
      if (p < 1) requestAnimationFrame(tick);
    })(t0);
  }

  var ctrObs = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) {
        runCounter(e.target);
        ctrObs.unobserve(e.target);
      }
    });
  }, { threshold: 0.6 });

  page.querySelectorAll('.ag-about-cta__stat').forEach(function (el) {
    ctrObs.observe(el);
  });

  /* ── 6. Founder background text parallax ───────────────────────────── */
  var bgText = page.querySelector('.ag-about-founder__bg-text');
  var founderSection = page.querySelector('.ag-about-founder');
  if (bgText && founderSection) {
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var rect = founderSection.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          var progress = -rect.top / (rect.height + window.innerHeight);
          bgText.style.transform = 'translateY(' + (progress * 60).toFixed(1) + 'px)';
        }
        ticking = false;
      });
    }, { passive: true });
  }

})();
